<script setup lang="ts">
import { computed, ref, type Ref, watch } from 'vue'
import { default as PSelect } from 'primevue/select'
import { default as PButton } from 'primevue/button'
import { default as PMessage } from 'primevue/message'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import type { Quiz } from '@/core/domain/quiz/Quiz.ts'
import type { QuizVersion } from '@/core/domain/quiz/QuizVersion.ts'
import { useQuizStore } from '@/presentation/stores/quiz/quiz.ts'
import { storeToRefs } from 'pinia'
import type { CreateQuizVersionPayload } from '@/core/useCases/quiz/CreateQuizVersionUseCase.ts'
import { useQuizAccessUtils } from '@/presentation/composables/quiz/useQuizAccessUtils.ts'

// Props
const props = defineProps<{
  quiz: Quiz
}>()

// Model - Version sélectionnée
const selectedVersion = defineModel<QuizVersion | null>('selectedVersion', {
  default: null
})

// Store
const quizStore = useQuizStore()
const { loading } = storeToRefs(quizStore)

// Composables
const confirm = useConfirm()
const toast = useToast()
const { canPublishQuizVersion } = useQuizAccessUtils()

// Emits
const emit = defineEmits<{
  'version-activated': [Quiz]
}>()

// Refs
const showCreateDialog: Ref<boolean> = ref(false)
const newVersionTitle: Ref<string> = ref('')
const newVersionComment: Ref<string> = ref('')
const copyFromVersion: Ref<QuizVersion | null> = ref(null)
// Ref pour forcer la réactivité lors des changements de questions
const questionsTrigger = ref(0)

// Watch pour détecter les changements dans les questions
watch(
  () => selectedVersion.value?.questions,
  () => {
    questionsTrigger.value++
  },
  { deep: true }
)

// Computed
const versionsOptions = computed(() => {
  return props.quiz.sortedVersions.map((version) => ({
    label: `${version.displayTitle} ${version.isActive ? '(Actif)' : version.isDraft ? '(Brouillon)' : '(Archivé)'}`,
    value: version,
    disabled: version.isArchived
  }))
})

// Computed pour synchroniser avec la version active du quiz
const displayedVersion = computed({
  get: () => {
    // Si on a une version sélectionnée, on l'utilise
    if (selectedVersion.value) {
      return selectedVersion.value
    }
    // Sinon on retourne la version active du quiz
    return props.quiz.activeVersion
  },
  set: (version: QuizVersion | null) => {
    // Simple sélection sans activation automatique
    if (version && version.id !== selectedVersion.value?.id) {
      selectedVersion.value = version
    }
  }
})

const canCreateVersion = computed(() => {
  return !props.quiz.hasDraftVersion
})

// Validation pour l'activation d'une version
const canActivateVersion = computed(() => {
  // Forcer la réévaluation quand les questions changent
  questionsTrigger.value

  if (!displayedVersion.value) return false
  if (displayedVersion.value.isArchived) return false
  if (displayedVersion.value.isActive) return false

  // Vérifier que la version a au moins 5 questions valides
  return isVersionValidForActivation(displayedVersion.value)
})

const activationValidationMessage = computed(() => {
  // Forcer la réévaluation quand les questions changent
  questionsTrigger.value

  if (!displayedVersion.value) return ''
  if (displayedVersion.value.isActive) return ''

  const validQuestions = getValidQuestionsCount(displayedVersion.value)
  const requiredQuestions = 5

  if (validQuestions < requiredQuestions) {
    const missing = requiredQuestions - validQuestions
    return `Cette version doit avoir au moins ${requiredQuestions} questions valides pour être activée (${missing} question${missing > 1 ? 's' : ''} manquante${missing > 1 ? 's' : ''})`
  }

  return ''
})

/**
 * Vérifie si on peut supprimer une version donnée
 */
const canDeleteVersion = computed(() => (version: QuizVersion) => {
  // On ne peut supprimer que les versions brouillon
  if (!version.isDraft) return false

  // Compter le nombre de versions brouillon
  const draftVersions = props.quiz.versions.filter((v) => v.isDraft)

  // Si c'est la seule version brouillon, on ne peut pas la supprimer
  if (draftVersions.length === 1 && draftVersions[0].id === version.id) {
    return false
  }

  return true
})

/**
 * Message d'erreur pour la suppression
 */
const deleteValidationMessage = computed(() => {
  if (!displayedVersion.value) return ''
  if (!displayedVersion.value.isDraft) return ''

  const draftVersions = props.quiz.versions.filter((v) => v.isDraft)
  if (draftVersions.length === 1 && draftVersions[0].id === displayedVersion.value.id) {
    return "Impossible de supprimer cette version car c'est la seule version brouillon disponible. Vous devez d'abord créer une autre version ou activer une version existante."
  }

  return ''
})

const copyOptions = computed(() => {
  const options: Array<{ label: string; value: QuizVersion | null }> = [
    {
      label: 'Créer une version vierge',
      value: null
    }
  ]

  // Ajouter toutes les versions existantes
  props.quiz.versions.forEach((version) => {
    options.push({
      label: `Copier depuis : ${version.displayTitle}`,
      value: version
    })
  })

  return options
})

// Computed pour le nombre de questions valides (réactif aux changements)
const validQuestionsCount = computed(() => {
  // Forcer la réévaluation quand les questions changent
  questionsTrigger.value

  return (version: QuizVersion) => getValidQuestionsCount(version)
})

// Methods

/**
 * Vérifie si une version a au moins 5 questions valides
 */
function isVersionValidForActivation(version: QuizVersion): boolean {
  const requiredQuestions = 5
  const validQuestions = getValidQuestionsCount(version)
  return validQuestions >= requiredQuestions
}

/**
 * Compte le nombre de questions valides dans une version
 */
function getValidQuestionsCount(version: QuizVersion): number {
  return version.questions.filter((question) => question.isValid()).length
}

/**
 * Retourne les erreurs de validation pour une version
 */
function getVersionValidationErrors(version: QuizVersion): string[] {
  const errors: string[] = []
  const validQuestions = getValidQuestionsCount(version)
  const requiredQuestions = 5

  if (validQuestions < requiredQuestions) {
    const missing = requiredQuestions - validQuestions
    errors.push(
      `Au moins ${requiredQuestions} questions valides sont requises (${missing} manquante${missing > 1 ? 's' : ''})`
    )
  }

  // Ajouter les erreurs spécifiques des questions invalides
  version.questions.forEach((question, index) => {
    if (!question.isValid()) {
      const questionErrors = question.getValidationErrors()
      questionErrors.forEach((error) => {
        errors.push(`Question ${index + 1}: ${error}`)
      })
    }
  })

  return errors
}

async function activateVersionIfNeeded(version: QuizVersion) {
  if (version.isArchived) return

  if (version.isDraft) {
    // Vérifier que la version a suffisamment de questions valides
    if (!isVersionValidForActivation(version)) {
      const errors = getVersionValidationErrors(version)
      console.error('Cannot activate version - validation errors:', errors)

      // Afficher un message d'erreur à l'utilisateur
      const errorMessage = errors.join('\n')
      // Note: Il faudrait probablement utiliser un toast ou un message d'erreur ici
      toast.add({
        severity: 'error',
        summary: 'Validation échouée',
        detail: errorMessage,
        life: 5000
      })
      return
    }

    // Activate draft versions
    const updatedQuiz = await quizStore.activateQuizVersion(props.quiz.id, version.id)
    selectedVersion.value = updatedQuiz.activeVersion

    // Notifier le composant parent qu'une version a été activée pour qu'il puisse rafraîchir les données
    emit('version-activated', updatedQuiz)
  }
  // If it's already active, no need to activate again
}

async function createNewVersion(copyId?: number) {
  const payload: CreateQuizVersionPayload = {
    fromVersionId: copyId
  }

  try {
    const newVersion = await quizStore.createQuizVersion(props.quiz.id, payload)
    showCreateDialog.value = false

    // Mettre à jour la version sélectionnée avec la nouvelle version
    selectedVersion.value = newVersion
  } catch (error) {
    console.error('Erreur lors de la création de la version:', error)
  }
}

function confirmDeleteVersion(version: QuizVersion) {
  if (!version.isDraft) {
    // Cannot delete non-draft version
    return
  }

  // Vérifier si on peut supprimer cette version
  if (!canDeleteVersion.value(version)) {
    toast.add({
      severity: 'warn',
      summary: 'Suppression impossible',
      detail:
        "Impossible de supprimer cette version car c'est la seule version brouillon disponible.",
      life: 5000
    })
    return
  }

  confirm.require({
    message: `Êtes-vous sûr de vouloir supprimer la version "${version.displayTitle}" ?`,
    header: 'Supprimer la version',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Supprimer',
    rejectLabel: 'Annuler',
    accept: () => deleteVersion(version),
    reject: () => {}
  })
}

async function deleteVersion(version: QuizVersion) {
  try {
    await quizStore.deleteQuizVersion(props.quiz.id, version.id)
    selectedVersion.value = props.quiz.activeVersion
  } catch (error) {
    console.error('Erreur lors de la suppression:', error)
  }
}

function getVersionStatusSeverity(version: QuizVersion) {
  if (version.isActive) return 'success'
  if (version.isDraft) return 'secondary'
  return 'warn'
}
</script>

<template>
  <div class="w-full">
    <!-- Version du quiz title -->
    <h3 class="mb-4 text-lg font-bold">Version du quiz</h3>

    <!-- Version selector -->
    <div class="mb-6">
      <div class="mb-4 flex-1">
        <p-select
          v-model="displayedVersion"
          :options="versionsOptions"
          option-label="label"
          option-value="value"
          placeholder="Sélectionner une version"
          class="w-full"
          :loading="loading.activateVersion"
        >
          <template #value="{ value }">
            <div v-if="value" class="flex items-center gap-2">
              <span>{{ value.displayTitle }}</span>
              <i
                v-if="value.isActive"
                class="pi pi-check-circle text-green-500"
                title="Version active"
              />
              <i v-else-if="value.isDraft" class="pi pi-pencil text-gray-500" title="Brouillon" />
              <i
                v-else-if="value.isArchived"
                class="pi pi-archive text-orange-500"
                title="Archivée"
              />
              <span
                v-if="value.id === selectedVersion?.id && !value.isActive"
                class="rounded bg-blue-100 px-2 py-1 text-xs text-blue-700"
                title="Version sélectionnée pour édition"
              >
                En édition
              </span>
            </div>
          </template>

          <template #option="{ option }">
            <div class="flex w-full items-center justify-between">
              <div class="flex items-center gap-2">
                <span>{{ option.value.displayTitle }}</span>
                <i v-if="option.value.isActive" class="pi pi-check-circle text-green-500" />
                <i v-else-if="option.value.isDraft" class="pi pi-pencil text-gray-500" />
                <i v-else-if="option.value.isArchived" class="pi pi-archive text-orange-500" />
              </div>

              <div class="flex items-center gap-1">
                <small class="text-gray-500">
                  {{ option.value.questionCount }} question{{
                    option.value.questionCount > 1 ? 's' : ''
                  }}
                </small>

                <!-- Informations sur les questions valides -->
                <small
                  v-if="option.value.isDraft"
                  class="rounded px-2 py-1 text-xs"
                  :class="
                    validQuestionsCount(option.value) >= 5
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  "
                >
                  {{ validQuestionsCount(option.value) }}/5 valides
                </small>
              </div>
            </div>
          </template>
        </p-select>
      </div>

      <!-- Action buttons -->
      <div class="flex items-center gap-2">
        <p-button
          v-if="canCreateVersion"
          icon="pi pi-plus"
          outlined
          @click="createNewVersion()"
          title="Créer une nouvelle version"
          :loading="loading.createVersion"
        />

        <p-button
          v-if="canCreateVersion"
          icon="pi pi-copy"
          outlined
          @click="createNewVersion(displayedVersion?.id)"
          :disabled="!displayedVersion"
          :title="
            displayedVersion
              ? 'Créer une version à partir de la version sélectionnée'
              : 'Aucune version sélectionnée'
          "
        />

        <p-button
          v-if="displayedVersion && !displayedVersion.isActive"
          class="ml-auto"
          label="Publier cette version"
          icon="pi pi-check-circle"
          :disabled="
            displayedVersion.isArchived ||
            !canActivateVersion ||
            !canPublishQuizVersion(displayedVersion)
          "
          @click="activateVersionIfNeeded(displayedVersion)"
          :loading="loading.activateVersion"
        />

        <div
          v-else-if="displayedVersion && displayedVersion.isActive"
          class="ml-auto flex items-center gap-2 text-green-600"
        >
          <i class="pi pi-check-circle"></i>
          <span class="text-sm font-medium">Version active</span>
        </div>
      </div>

      <!-- Message de validation pour l'activation -->
      <p-message v-if="activationValidationMessage" severity="warn" :closable="true" class="mt-4">
        {{ activationValidationMessage }}
      </p-message>

      <!-- Message d'information si on ne peut pas créer de version -->
      <p-message
        v-if="props.quiz.hasDraftVersion && props.quiz.hasVersions"
        severity="info"
        :closable="true"
        class="mt-4"
      >
        Une version brouillon existe déjà. Vous devez d'abord publier cette version ou supprimer ses
        questions brouillon avant de créer une nouvelle version.
      </p-message>
    </div>
  </div>
</template>
